/**
 * I-1470 (`docs/concept-failure-messages.md` EM-12, EM-13): the id the server gives a failure, which
 * is the one reference a person reads, says aloud or copies, and the words every surface shows it
 * with.
 *
 * ⭐ **Shown as minted.** The server mints the id short enough to read out and logs it whole, so the
 * text on the screen is the text a search of the log finds; no surface shortens or reshapes it.
 *
 * Ten characters of Crockford's base-32 alphabet, lowercase: digits and letters without i, l, o and
 * u, so none reads as another when said aloud. 32^10 is about 1.1 × 10^15 ids; at a million failures
 * a day, a repeat is expected after about 46,000 days of logs (the birthday bound, √(2·32^10)).
 */

/** The letters an id is made of: Crockford's base 32, lowercase. */
export const REQUEST_ID_ALPHABET = '0123456789abcdefghjkmnpqrstvwxyz';

/** How many letters a minted id has. */
export const REQUEST_ID_LENGTH = 10;

const MINTED = new RegExp(`^[${REQUEST_ID_ALPHABET}]{${REQUEST_ID_LENGTH}}$`);

/** Whether a string is an id as the server mints it. */
export function isMintedRequestId(value: string): boolean {
  return MINTED.test(value);
}

/**
 * An id from 32 random bytes or more, as minted. The caller brings the randomness (a platform's own
 * secure source), so this file stays free of any platform.
 */
export function requestIdFromRandomBytes(bytes: ArrayLike<number>): string {
  if (bytes.length < REQUEST_ID_LENGTH) {
    throw new Error(`A request id needs ${REQUEST_ID_LENGTH} random bytes.`);
  }
  let id = '';
  // 256 is a multiple of 32, so each byte's low five bits are uniform over the alphabet.
  for (let index = 0; index < REQUEST_ID_LENGTH; index += 1) {
    id += REQUEST_ID_ALPHABET[bytes[index] & 31];
  }
  return id;
}

/** What the id is called wherever it is shown. One fact, one phrase. */
export const REQUEST_ID_LABEL = 'Request id';

/** The words every surface shows the id with: `Request id 7k2m9qx4ab`. */
export function requestIdText(id: string): string {
  return `${REQUEST_ID_LABEL} ${id}`;
}

/** The id a shown text carries, as `requestIdText` wrote it; null when it carries none. */
export function readShownRequestId(text: string): string | null {
  return /Request id ([A-Za-z0-9._:-]{1,128})/.exec(text)?.[1]?.replace(/\.$/, '') ?? null;
}
